(function () {
  "use strict";

  const CARD_SELECTOR =
    "#collection-grid .product-home-card[data-product-handle]";

  const productCache = new Map();
  const queuedCards = new WeakSet();

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function shopRoot() {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root;
    }

    return "/";
  }

  function isMobileViewport() {
    return (
      window.matchMedia &&
      window.matchMedia("(max-width: 768px)").matches
    );
  }

  function formatMoney(cents) {
    const currency =
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
        ? window.Shopify.currency.active
        : "USD";

    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: currency,
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return "$" + ((Number(cents) || 0) / 100).toFixed(2);
    }
  }

  function getEmbeddedProduct(card) {
    if (!card || !card.querySelector) {
      return null;
    }

    const script = card.querySelector("script.wfs-v3-product-data");

    if (!script) {
      return null;
    }

    try {
      const product = normalizeProductJson(JSON.parse(script.textContent));
      const sizeGuide = readEmbeddedSizeGuide(card);

      if (product && sizeGuide) {
        product.wfsSizeGuide = sizeGuide;
      }

      return product;
    } catch (error) {
      return null;
    }
  }

  function readEmbeddedSizeGuide(card) {
    const script = card.querySelector("script.wfs-v3-size-guide");

    if (!script || !script.textContent || !script.textContent.trim()) {
      return null;
    }

    try {
      const value = JSON.parse(script.textContent);

      return value || null;
    } catch (error) {
      return null;
    }
  }

  function parseSizeGuideValue(value) {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    return String(value)
      .split("/")
      .map(function (item) {
        return item.trim();
      })
      .filter(Boolean);
  }

  function normalizeProductJson(product) {
    if (!product || typeof product !== "object") {
      return product;
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];

    variants.forEach(function (variant) {
      if (!variant || Array.isArray(variant.options)) {
        return;
      }

      const options = [];

      if (variant.option1 != null) options.push(variant.option1);
      if (variant.option2 != null) options.push(variant.option2);
      if (variant.option3 != null) options.push(variant.option3);

      variant.options = options;
    });

    if (!Array.isArray(product.images) && product.featured_image) {
      product.images = [product.featured_image];
    }

    return product;
  }

  function getProduct(handle, card) {
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const embedded = getEmbeddedProduct(card);

    if (embedded) {
      const resolved = Promise.resolve(embedded);
      productCache.set(handle, resolved);
      return resolved;
    }

    const request = fetch(
      shopRoot() + "products/" + encodeURIComponent(handle) + ".js",
      {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      }
    ).then(function (response) {
      if (!response.ok) {
        throw new Error("Unable to load product " + handle);
      }

      return response.json();
    }).then(normalizeProductJson);

    productCache.set(handle, request);
    return request;
  }

  function normalizeOptions(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];

    return (product.options || []).map(function (option, index) {
      const name =
        typeof option === "string"
          ? option
          : option && option.name
          ? option.name
          : "Option " + (index + 1);

      let values =
        option && Array.isArray(option.values) ? option.values.slice() : [];

      if (!values.length) {
        values = variants
          .map(function (variant) {
            return variant.options && variant.options[index];
          })
          .filter(function (value, valueIndex, allValues) {
            return value && allValues.indexOf(value) === valueIndex;
          });
      }

      return {
        name: name,
        values: values,
        index: index,
        type: optionType(name, values),
      };
    });
  }

  function foldOptionText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/ß/g, "ss")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  function nameIncludesAny(normalized, keys) {
    return keys.some(function (key) {
      return normalized.indexOf(key) !== -1;
    });
  }

  function inferTypeFromValues(values) {
    const list = (values || [])
      .map(function (value) {
        return String(value || "").trim();
      })
      .filter(Boolean);

    if (!list.length) {
      return "";
    }

    function matchRatio(pattern) {
      return (
        list.filter(function (value) {
          return pattern.test(value);
        }).length / list.length
      );
    }

    if (matchRatio(/^\d+\s*[ev](\s*[-–/]\s*\d+\s*[ev])?$/i) >= 0.5) {
      return "width";
    }

    if (matchRatio(/^(uk|eu|us)?\s*\d+([.,]\d+)?\s*(uk|eu|us)?$/i) >= 0.5) {
      return "size";
    }

    return "";
  }

  function optionType(name, values) {
    const normalized = foldOptionText(name);

    if (normalized === "title") {
      return "other";
    }

    if (
      nameIncludesAny(normalized, [
        "size",
        "tamano",
        "talla",
        "taglia",
        "taille",
        "grosse",
        "groesse",
        "tamanho",
        "maat",
      ])
    ) {
      return "size";
    }

    if (
      nameIncludesAny(normalized, [
        "width",
        "ancho",
        "anchura",
        "larghezza",
        "largeur",
        "breite",
        "largura",
        "breedte",
      ])
    ) {
      return "width";
    }

    if (
      nameIncludesAny(normalized, [
        "colour",
        "color",
        "colore",
        "couleur",
        "farbe",
        "kleur",
      ]) ||
      normalized === "cor"
    ) {
      return "colour";
    }

    return inferTypeFromValues(values) || "other";
  }

  function getRenderableOptions(options) {
    const groups = {
      size: [],
      width: [],
      colour: [],
    };

    options.forEach(function (option) {
      const type = option.type || optionType(option.name, option.values);

      if (groups[type]) {
        groups[type].push(option);
      }
    });

    const ordered = groups.size.concat(groups.width);

    if (!isMobileViewport()) {
      ordered.push.apply(ordered, groups.colour);
    }

    return ordered;
  }

  function swatchColour(name) {
    const value = String(name || "").toLowerCase();

    const colours = {
      navy: "#172554",
      blue: "#2563eb",
      black: "#18181b",
      white: "#f8fafc",
      cream: "#fff7dd",
      ivory: "#fffff0",
      red: "#dc2626",
      burgundy: "#7f1d1d",
      maroon: "#7f1d1d",
      green: "#3f6212",
      olive: "#65733d",
      khaki: "#a39b68",
      tan: "#b98b5f",
      brown: "#754c2e",
      beige: "#d6c4a8",
      grey: "#71717a",
      gray: "#71717a",
      silver: "#bfc3c9",
      gold: "#c79a2b",
      pink: "#ec8ca8",
      purple: "#7e22ce",
      orange: "#ea580c",
      yellow: "#eab308",
    };

    const key = Object.keys(colours).find(function (colourName) {
      return value.indexOf(colourName) !== -1;
    });

    if (key) {
      return colours[key];
    }

    let hash = 0;

    for (let i = 0; i < value.length; i += 1) {
      hash = value.charCodeAt(i) + ((hash << 5) - hash);
    }

    return "hsl(" + Math.abs(hash % 360) + " 35% 48%)";
  }

  function getImageUrl(image) {
    if (!image) return "";
    if (typeof image === "string") return sizedImageUrl(image);
    if (typeof image.src === "string") return sizedImageUrl(image.src);
    if (typeof image.url === "string") return sizedImageUrl(image.url);

    return "";
  }

  function sizedImageUrl(url, width) {
    if (!url) return "";

    width = width || 720;

    const base = String(url).split("?")[0];

    return base + "?width=" + width;
  }

  function cleanImageUrl(url) {
    return String(url || "")
      .split("?")[0]
      .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|master)\./, ".");
  }

  function calculateDiscount(price, compareAtPrice) {
    price = Number(price) || 0;
    compareAtPrice = Number(compareAtPrice) || 0;

    if (!price || compareAtPrice <= price) {
      return 0;
    }

    return Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
  }

  /** Normalize width labels so "4 E" / "4E" / "4e" compare equal. */
  function normalizeWidthValue(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/\s+/g, "");
  }

  /**
   * Active Width facet values from the collection URL
   * (e.g. filter.v.option.width=4E). Multiple values = OR.
   */
  function getActiveWidthFilters() {
    const params = new URLSearchParams(window.location.search);
    const widths = [];

    params.forEach(function (value, key) {
      if (/option\.width$/i.test(key) || /\.width$/i.test(key)) {
        const normalized = normalizeWidthValue(value);
        if (normalized && widths.indexOf(normalized) === -1) {
          widths.push(normalized);
        }
      }
    });

    return widths;
  }

  /**
   * Active Size facet values from the collection URL
   * (e.g. filter.v.option.size=7). Multiple values = OR.
   * On US store these are product variant Size values (US sizes).
   */
  function normalizeSizeValue(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");
  }

  function getActiveSizeFilters() {
    const params = new URLSearchParams(window.location.search);
    const sizes = [];

    params.forEach(function (value, key) {
      if (/option\.size$/i.test(key) || (key.indexOf("filter.") === 0 && /\.size$/i.test(key))) {
        const normalized = normalizeSizeValue(value);
        if (normalized && sizes.indexOf(normalized) === -1) {
          sizes.push(normalized);
        }
      }
    });

    return sizes;
  }

  function getWidthOptionIndex(product) {
    const options = product.options || [];

    return options.findIndex(function (option) {
      const name =
        typeof option === "string"
          ? option
          : option && option.name
          ? option.name
          : "";
      const values =
        option && Array.isArray(option.values) ? option.values : [];

      return optionType(name, values) === "width";
    });
  }

  function getSizeOptionIndex(product) {
    const options = product.options || [];

    return options.findIndex(function (option) {
      const name =
        typeof option === "string"
          ? option
          : option && option.name
          ? option.name
          : "";
      const values =
        option && Array.isArray(option.values) ? option.values : [];

      return optionType(name, values) === "size";
    });
  }

  function pickPreferredVariant(pool, sortPrice) {
    const matchingSortPrice = pool.find(function (variant) {
      return Number(variant.price) === sortPrice;
    });
    if (matchingSortPrice) {
      return matchingSortPrice;
    }

    return pool.slice().sort(function (a, b) {
      return (Number(a.price) || 0) - (Number(b.price) || 0);
    })[0];
  }

  /**
   * Pick initial variant so displayed price aligns with Shopify collection
   * price sort (product.price = price_min), without reordering the grid.
   * Prefer available variant at sort price, else cheapest available.
   * When Width and/or Size filters are active, only consider available
   * variants that match them; return null if none (caller hides the card).
   */
  function getInitialVariant(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) {
      return {};
    }

    const sortPrice = Number(product.price) || 0;
    const filteredWidths = getActiveWidthFilters();
    const filteredSizes = getActiveSizeFilters();
    const widthIndex = getWidthOptionIndex(product);
    const sizeIndex = getSizeOptionIndex(product);

    if (filteredWidths.length || filteredSizes.length) {
      if (filteredWidths.length && widthIndex === -1) {
        return null;
      }

      if (filteredSizes.length && sizeIndex === -1) {
        return null;
      }

      const matchingAvailable = variants.filter(function (variant) {
        if (!variant.available || !variant.options) {
          return false;
        }

        if (filteredWidths.length) {
          const widthValue = normalizeWidthValue(variant.options[widthIndex]);
          if (filteredWidths.indexOf(widthValue) === -1) {
            return false;
          }
        }

        if (filteredSizes.length) {
          const sizeValue = normalizeSizeValue(variant.options[sizeIndex]);
          if (filteredSizes.indexOf(sizeValue) === -1) {
            return false;
          }
        }

        return true;
      });

      if (!matchingAvailable.length) {
        return null;
      }

      return pickPreferredVariant(matchingAvailable, sortPrice);
    }

    const available = variants.filter(function (variant) {
      return variant.available;
    });
    const pool = available.length ? available : variants;

    return pickPreferredVariant(pool, sortPrice);
  }

  function optionName(option) {
    if (typeof option === "string") {
      return option;
    }

    return (option && option.name) || "";
  }

  function isWomensProduct(product) {
    const tags = Array.isArray(product.tags)
      ? product.tags.join(":")
      : String(product.tags || "");
    const joined = (":" + tags + ":").toLowerCase();

    return (
      joined.indexOf(":womens:") !== -1 ||
      joined.indexOf(":gender_womens:") !== -1
    );
  }

  function getSizeGuideArray(product) {
    const embedded = parseSizeGuideValue(product && product.wfsSizeGuide);

    if (embedded.length) {
      return embedded;
    }

    const data = window.sizeGuideData || {};
    const brand = String((product && product.vendor) || "")
      .replace(/\s+/g, "_")
      .toLowerCase();
    const gender = isWomensProduct(product) ? "women" : "men";
    const brandKey = brand + "_" + gender;
    let value = data[brandKey];

    if (!value || (typeof value === "string" && !value.trim())) {
      value = data[gender];
    }

    return parseSizeGuideValue(value);
  }

  function findSizeOptionIndex(product) {
    const options = product.options || [];

    return options.findIndex(function (option) {
      return optionName(option).toLowerCase().indexOf("size") !== -1;
    });
  }

  function getSizeConversions(sizeValue, product) {
    const numericSize = parseFloat(sizeValue);
    const fallback = {
      uk: sizeValue,
      eu: sizeValue,
      us: sizeValue,
      matched: false,
    };

    if (isNaN(numericSize)) {
      return fallback;
    }

    const sizeGuideArray = getSizeGuideArray(product);

    if (!sizeGuideArray.length) {
      return fallback;
    }

    const sizeOptionIndex = findSizeOptionIndex(product);
    let firstVariantValue = 0;

    if (sizeOptionIndex !== -1 && Array.isArray(product.variants)) {
      const firstVariant = product.variants.find(function (variant) {
        return (
          variant.options &&
          variant.options[sizeOptionIndex] != null &&
          variant.options[sizeOptionIndex] !== ""
        );
      });

      if (firstVariant) {
        firstVariantValue =
          parseFloat(firstVariant.options[sizeOptionIndex]) || 0;
      }
    }

    // Match the product page: UK column, or EU when the catalog size is over 30.
    // Never match the US column, or an in-stock catalog size is labeled as a different US size.
    const matchIndex = firstVariantValue > 30 ? 1 : 0;

    for (let j = 0; j < sizeGuideArray.length; j++) {
      const parts = String(sizeGuideArray[j])
        .split("|")
        .map(function (part) {
          return part.trim();
        });

      if (parts.length < 3) {
        continue;
      }

      const convertedSize = parseFloat(parts[matchIndex]) || 0;

      if (Math.abs(convertedSize - numericSize) < 0.01) {
        return {
          uk: parts[0] || sizeValue,
          eu: parts[1] || sizeValue,
          us: parts[2] || sizeValue,
          matched: true,
        };
      }
    }

    return fallback;
  }

  function buildSizeProperty(product, variant) {
    const sizeOptionIndex = findSizeOptionIndex(product);

    if (sizeOptionIndex === -1 || !variant || !variant.options) {
      return null;
    }

    const sizeValue = variant.options[sizeOptionIndex];

    if (sizeValue == null || sizeValue === "") {
      return null;
    }

    const conversions = getSizeConversions(sizeValue, product);

    return (
      "US - " +
      conversions.us +
      " | UK - " +
      conversions.uk +
      " | EU - " +
      conversions.eu
    );
  }

  function updateSaleBadge(card, product, variant) {
    const badgeCorner = card.querySelector(".product-badge-corner");

    if (!badgeCorner) {
      return;
    }

    const badgeText = badgeCorner.querySelector(".badge-text");

    if (!product.available) {
      badgeCorner.hidden = false;
      badgeCorner.classList.add("wfs-v3__sale-badge");

      if (badgeText) {
        badgeText.textContent = "SOLD OUT";
      }

      return;
    }

    const price = Number(variant.price || product.price) || 0;
    const compareAtPrice = Number(variant.compare_at_price || product.compare_at_price) || 0;
    const discount = calculateDiscount(price, compareAtPrice);

    if (!discount) {
      badgeCorner.hidden = true;
      return;
    }

    badgeCorner.hidden = false;
    badgeCorner.classList.add("wfs-v3__sale-badge");

    if (badgeText) {
      badgeText.textContent = "SALE -" + discount + "%";
    }
  }

  function selectionIsOpen(selectedOptions, index) {
    return selectedOptions[index] == null || selectedOptions[index] === "";
  }

  function sortOptionValues(values, option, product) {
    const type = option.type || optionType(option.name, option.values);

    return values.slice().sort(function (a, b) {
      if (type === "size" && product) {
        const aUs = parseFloat(getSizeConversions(a, product).us);
        const bUs = parseFloat(getSizeConversions(b, product).us);

        if (!isNaN(aUs) && !isNaN(bUs) && aUs !== bUs) {
          return aUs - bUs;
        }
      }

      return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }

  function optionIsSize(option) {
    const type = option.type || optionType(option.name, option.values);
    return type === "size";
  }

  /**
   * A catalog size with no guide row must not reuse another size's US label.
   */
  function withoutCollidingSizeLabels(values, option, product) {
    if (!product || !optionIsSize(option)) {
      return values;
    }

    const matchedLabels = {};

    (option.values || []).forEach(function (value) {
      const conversion = getSizeConversions(value, product);

      if (conversion.matched) {
        matchedLabels[String(conversion.us)] = true;
      }
    });

    return values.filter(function (value) {
      const conversion = getSizeConversions(value, product);

      if (conversion.matched) {
        return true;
      }

      return !matchedLabels[String(value)];
    });
  }

  function getAvailableValues(option, variants, selectedOptions) {
    return option.values.filter(function (value) {
      return variants.some(function (variant) {
        if (!variant.available || !variant.options) {
          return false;
        }

        return variant.options.every(function (variantValue, index) {
          if (index === option.index) {
            return variantValue === value;
          }

          return (
            selectionIsOpen(selectedOptions, index) ||
            variantValue === selectedOptions[index]
          );
        });
      });
    });
  }

  function makeOptionMarkup(option, selectedValue, availableValues, product) {
    const type = option.type || optionType(option.name, option.values);

    if (type === "other" || String(option.name).toLowerCase() === "title") {
      return "";
    }

    if (!availableValues.length) {
      return "";
    }

    const label =
      type === "size"
        ? "Sizes available"
        : type === "width"
        ? "Widths available"
        : "Colours available";

    const buttons = availableValues
      .map(function (value) {
        const selected = value === selectedValue ? " is-selected" : "";
        const pressed = value === selectedValue ? "true" : "false";

        if (type === "colour") {
          return (
            '<button type="button"' +
            ' class="wfs-v3__colour-button' +
            selected +
            '"' +
            ' data-option-index="' +
            option.index +
            '"' +
            ' data-option-type="colour"' +
            ' data-option-value="' +
            escapeHtml(value) +
            '"' +
            ' aria-label="' +
            escapeHtml(value) +
            '"' +
            ' aria-pressed="' +
            pressed +
            '"' +
            ' style="--wfs-swatch:' +
            swatchColour(value) +
            '"></button>'
          );
        }

        // Size buttons show US labels; data-option-value stays the catalog size.
        const displayValue =
          type === "size"
            ? getSizeConversions(value, product).us
            : value;

        return (
          '<button type="button"' +
          ' class="wfs-v3__option-button' +
          selected +
          '"' +
          ' data-option-index="' +
          option.index +
          '"' +
          ' data-option-type="' +
          type +
          '"' +
          ' data-option-value="' +
          escapeHtml(value) +
          '"' +
          ' aria-label="' +
          escapeHtml(displayValue) +
          '"' +
          ' aria-pressed="' +
          pressed +
          '">' +
          escapeHtml(displayValue) +
          "</button>"
        );
      })
      .join("");

    return (
      '<div class="wfs-v3__option wfs-v3__option--' +
      type +
      '" data-option-group="' +
      option.index +
      '">' +
      '<div class="wfs-v3__option-header">' +
      '<span class="wfs-v3__option-label">' +
      label +
      "</span>" +
      (type === "colour"
        ? '<span class="wfs-v3__colour-name">' + escapeHtml(selectedValue || "") + "</span>"
        : "") +
      "</div>" +
      '<div class="wfs-v3__values">' +
      buttons +
      "</div>" +
      "</div>"
    );
  }

  function renderOptionGroups(container, options, selectedOptions, variants, product, depth) {
    const optionsHost = container.querySelector(".wfs-v3__options");

    if (!optionsHost) {
      return;
    }

    let selectionChanged = false;
    const markup = options
    .map(function (option) {
      const availableValues = sortOptionValues(
        withoutCollidingSizeLabels(
          getAvailableValues(option, variants, selectedOptions),
          option,
          product
        ),
        option,
        product
      );
      let selectedValue = selectedOptions[option.index];

      if (availableValues.indexOf(selectedValue) === -1) {
        selectedValue = availableValues[0] || "";

        if (selectedOptions[option.index] !== selectedValue) {
          selectedOptions[option.index] = selectedValue;
          selectionChanged = true;
        }
      }

      return makeOptionMarkup(option, selectedValue, availableValues, product);
    })
    .join("");

    if (selectionChanged && (depth || 0) < 3) {
      renderOptionGroups(
        container,
        options,
        selectedOptions,
        variants,
        product,
        (depth || 0) + 1
      );
      return;
    }

    optionsHost.innerHTML = markup;
  }

  function showCardSkeleton(card, options) {
    if (!card || card.querySelector(".wfs-v3__skeleton")) {
      return;
    }

    const wrapper = card.querySelector(".product-home-card__wrapper");

    if (!wrapper) {
      return;
    }

    const keepImageVisible = options && options.keepImageVisible;

    card.classList.add("wfs-v3__loading");

    if (keepImageVisible) {
      card.classList.add("wfs-v3__loading--keep-image");
    }

    const skeleton = document.createElement("div");
    skeleton.className =
      "wfs-v3__skeleton" + (keepImageVisible ? " wfs-v3__skeleton--body-only" : "");
    skeleton.setAttribute("aria-hidden", "true");
    skeleton.innerHTML =
      (keepImageVisible ? "" : '<div class="wfs-v3__skeleton-image"></div>') +
      '<div class="wfs-v3__skeleton-content">' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--brand"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--title"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--title-short"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--price"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--label"></div>' +
      '<div class="wfs-v3__skeleton-grid"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--label"></div>' +
      '<div class="wfs-v3__skeleton-grid wfs-v3__skeleton-grid--width"></div>' +
      '<div class="wfs-v3__skeleton-line wfs-v3__skeleton-line--label"></div>' +
      '<div class="wfs-v3__skeleton-swatch"></div>' +
      '<div class="wfs-v3__skeleton-button"></div>' +
      '<div class="wfs-v3__skeleton-button wfs-v3__skeleton-button--ghost"></div>' +
      "</div>";

    wrapper.appendChild(skeleton);
  }

  function removeCardSkeleton(card) {
    if (!card) {
      return;
    }

    card.classList.remove("wfs-v3__loading", "wfs-v3__loading--keep-image");

    const skeleton = card.querySelector(".wfs-v3__skeleton");

    if (skeleton) {
      skeleton.remove();
    }
  }

  function cleanProductTitle(title, vendor) {
    let cleaned = String(title || "");

    // Remove vendor name (case-insensitive, whole words)
    if (vendor) {
      const escapedVendor = String(vendor).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      cleaned = cleaned.replace(new RegExp("\\b" + escapedVendor + "\\b", "gi"), "");
    }

    // Remove "Wide fit" / "Wide-Fit" etc.
    cleaned = cleaned.replace(/\bwide[\s-]+fit\b/gi, "");

    // Tidy leftover spaces / separators
    cleaned = cleaned
      .replace(/\s{2,}/g, " ")
      .replace(/^[\s\-–—,|/]+|[\s\-–—,|/]+$/g, "")
      .trim();

    return cleaned || String(title || "");
  }

  function buildImages(imageContainer, product, productLink, state) {
    const urls = (product.images || [])
      .map(getImageUrl)
      .filter(Boolean)
      .filter(function (url, index, allUrls) {
        return (
          allUrls.findIndex(function (otherUrl) {
            return cleanImageUrl(otherUrl) === cleanImageUrl(url);
          }) === index
        );
      });

    if (!urls.length && product.featured_image) {
      urls.push(getImageUrl(product.featured_image));
    }

    if (!urls.length) return;

    state.images = urls;

    let media = productLink.closest(".wfs-v3__media");

    if (!media) {
      media = document.createElement("div");
      media.className = "wfs-v3__media";

      productLink.parentNode.insertBefore(media, productLink);
      media.appendChild(productLink);
    }

    const existingImg = imageContainer.querySelector("img");
    const existingSrc = existingImg
      ? existingImg.currentSrc || existingImg.getAttribute("src") || ""
      : "";
    const reuseFirstImage =
      existingSrc &&
      urls[0] &&
      cleanImageUrl(existingSrc) === cleanImageUrl(urls[0]);

    imageContainer.innerHTML = urls
      .map(function (url, index) {
        const src =
          index === 0 && reuseFirstImage ? existingSrc : url;

        return (
          '<img class="wfs-v3__image' +
          (index === 0 ? " is-active" : "") +
          '" src="' +
          escapeHtml(src) +
          '" alt="' +
          escapeHtml(product.title || "Product image") +
          " - image " +
          (index + 1) +
          '"' +
          (index === 0 ? ' loading="eager"' : ' loading="lazy"') +
          ">"
        );
      })
      .join("");

    if (urls.length > 1) {
      media.insertAdjacentHTML(
        "beforeend",
        '<div class="wfs-v3__image-counter" aria-live="polite">' +
          '<span class="wfs-v3__image-current">1</span> / ' +
          urls.length +
          "</div>" +
          '<div class="wfs-v3__image-nav">' +
          '<div class="wfs-v3__image-controls">' +
          '<button type="button" class="wfs-v3__image-arrow wfs-v3__image-arrow--previous" aria-label="Previous image">&#8249;</button>' +
          '<button type="button" class="wfs-v3__image-arrow wfs-v3__image-arrow--next" aria-label="Next image">&#8250;</button>' +
          "</div>" +
          '<div class="wfs-v3__image-progress">' +
          '<div class="wfs-v3__image-progress-track">' +
          '<div class="wfs-v3__image-progress-fill"></div>' +
          "</div>" +
          "</div>" +
          "</div>"
      );
    }

    const images = imageContainer.querySelectorAll(".wfs-v3__image");
    const currentCounter = media.querySelector(".wfs-v3__image-current");
    const progressFill = media.querySelector(".wfs-v3__image-progress-fill");
    const previousButton = media.querySelector(".wfs-v3__image-arrow--previous");
    const nextButton = media.querySelector(".wfs-v3__image-arrow--next");

    state.showImage = function (index) {
      if (!images.length) return;

      if (index < 0) {
        index = images.length - 1;
      }

      if (index >= images.length) {
        index = 0;
      }

      state.currentImageIndex = index;

      images.forEach(function (image, imageIndex) {
        image.classList.toggle("is-active", imageIndex === index);
      });

      if (currentCounter) {
        currentCounter.textContent = index + 1;
      }

      if (progressFill) {
        progressFill.style.width = ((index + 1) / images.length) * 100 + "%";
      }
    };

    function changeImage(direction) {
      state.showImage(state.currentImageIndex + direction);
      state.baseImageIndex = state.currentImageIndex;
    }

    if (previousButton) {
      previousButton.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        changeImage(-1);
        event.currentTarget.blur();
      });
    }

    if (nextButton) {
      nextButton.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        changeImage(1);
        event.currentTarget.blur();
      });
    }

    state.showImage(0);
    bindImageSwipe(media, state);
  }

  function bindImageSwipe(media, state) {
    if (!media || state.images.length <= 1) {
      return;
    }

    let touchStartX = 0;
    let touchStartY = 0;

    media.addEventListener(
      "touchstart",
      function (event) {
        if (!event.touches.length) {
          return;
        }

        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      },
      { passive: true }
    );

    media.addEventListener(
      "touchend",
      function (event) {
        if (!event.changedTouches.length) {
          return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStartX;
        const deltaY = touch.clientY - touchStartY;

        if (Math.abs(deltaX) < 30 || Math.abs(deltaX) < Math.abs(deltaY)) {
          return;
        }

        state.showImage(state.currentImageIndex + (deltaX < 0 ? 1 : -1));
        state.baseImageIndex = state.currentImageIndex;
      },
      { passive: true }
    );
  }

  function updatePrice(body, product, variant) {
    const price = Number(variant.price || product.price) || 0;
    const compareAtPrice = Number(variant.compare_at_price || product.compare_at_price) || 0;
    const discount = calculateDiscount(price, compareAtPrice);

    const priceElement = body.querySelector(".wfs-v3__price");
    const compareElement = body.querySelector(".wfs-v3__compare-price");
    const discountElement = body.querySelector(".wfs-v3__discount");

    if (priceElement) {
      priceElement.textContent = formatMoney(price);
    }

    if (compareElement) {
      compareElement.textContent = compareAtPrice > price ? formatMoney(compareAtPrice) : "";
      compareElement.hidden = compareAtPrice <= price;
    }

    if (discountElement) {
      discountElement.textContent = discount ? "-" + discount + "%" : "";
      discountElement.hidden = !discount;
    }
  }

  function refreshCartUI(addResponse) {
    fetch(shopRoot() + "cart.js", {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    })
      .then(function (response) {
        if (response && response.ok) {
          return response.json();
        }

        return null;
      })
      .then(function (cart) {
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            bubbles: true,
            detail: {
              product: addResponse,
              source: "wfs-product-card-v3",
            },
          })
        );

        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            bubbles: true,
            detail: {
              cart: cart,
              source: "wfs-product-card-v3",
            },
          })
        );

        document.dispatchEvent(
          new CustomEvent("cart:open", {
            bubbles: true,
          })
        );
      })
      .catch(function () {
        document.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            bubbles: true,
            detail: {
              product: addResponse,
              source: "wfs-product-card-v3",
            },
          })
        );

        document.dispatchEvent(
          new CustomEvent("cart:open", {
            bubbles: true,
          })
        );
      });
  }

  function isCardHiddenByOptionFilter(card) {
    if (!card) return false;

    if (
      card.dataset.wfsV3 === "hidden-option-filter" ||
      card.dataset.wfsV3 === "hidden-width-filter"
    ) {
      return true;
    }

    if (!getActiveWidthFilters().length && !getActiveSizeFilters().length) {
      return false;
    }

    const product = getEmbeddedProduct(card);
    if (!product) return false;

    return getInitialVariant(product) === null;
  }

  function syncPlpResultStats() {
    const statsNodes = document.querySelectorAll("[data-wfs-plp-stats]");
    if (!statsNodes.length) return;

    const cards = document.querySelectorAll("#collection-grid .product-home-card");
    let hidden = 0;

    cards.forEach(function (card) {
      if (isCardHiddenByOptionFilter(card)) hidden += 1;
    });

    const sample = statsNodes[0];
    const offset = parseInt(sample.getAttribute("data-wfs-stats-offset"), 10) || 0;
    const serverTotal = parseInt(sample.getAttribute("data-wfs-stats-total"), 10) || 0;
    const visible = Math.max(0, cards.length - hidden);
    let start = 0;
    let end = 0;
    let total = 0;

    if (visible > 0) {
      start = offset + 1;
      end = offset + visible;
      total = Math.max(end, serverTotal - hidden);
    }

    statsNodes.forEach(function (node) {
      const timeEl = node.querySelector("[data-wfs-plp-stats-time]");
      const timeText = timeEl && timeEl.textContent ? timeEl.textContent : "0.000s";

      node.textContent = "";
      node.appendChild(document.createTextNode(start + " - " + end + " out of "));

      const strong = document.createElement("strong");
      strong.textContent = total + " results found";
      node.appendChild(strong);

      node.appendChild(document.createTextNode(" in "));

      const time = document.createElement("span");
      time.setAttribute("data-wfs-plp-stats-time", "");
      time.textContent = timeText;
      node.appendChild(time);
    });
  }

  let plpStatsSyncFrame = 0;

  function schedulePlpResultStats() {
    if (plpStatsSyncFrame) return;

    plpStatsSyncFrame = window.requestAnimationFrame(function () {
      plpStatsSyncFrame = 0;
      syncPlpResultStats();
    });
  }

  function transformCard(card, product) {
    if (card.dataset.wfsV3 === "ready") return;

    const wrapper = card.querySelector(".product-home-card__wrapper");
    const productLink = card.querySelector(".product-home-card__image-link");
    const imageContainer = card.querySelector(".product-home-card__image");
    const oldBody = card.querySelector(".product-home-card__body");

    if (!wrapper || !productLink || !imageContainer || !oldBody) {
      removeCardSkeleton(card);
      return;
    }

    removeCardSkeleton(card);

    const variants = Array.isArray(product.variants) ? product.variants : [];

    const initialVariant = getInitialVariant(product);

    // Strict width/size filter: no in-stock match for active facet → hide card
    if (initialVariant === null) {
      card.style.display = "none";
      card.dataset.wfsV3 = "hidden-option-filter";
      schedulePlpResultStats();
      return;
    }

    card.style.display = "";
    if (
      card.dataset.wfsV3 === "hidden-width-filter" ||
      card.dataset.wfsV3 === "hidden-option-filter"
    ) {
      delete card.dataset.wfsV3;
    }

    const options = getRenderableOptions(normalizeOptions(product));

    const selectedOptions = (initialVariant.options || options.map(function (option) {
      return option.values[0];
    })).slice();

    const state = {
      images: [],
      baseImageIndex: 0,
      currentImageIndex: 0,
      showImage: function () {},
    };

    buildImages(imageContainer, product, productLink, state);
    updateSaleBadge(card, product, initialVariant);

    const discount = calculateDiscount(
      initialVariant.price || product.price,
      initialVariant.compare_at_price || product.compare_at_price
    );

    const newBody = document.createElement("div");
    newBody.className = "wfs-v3__body";

    newBody.innerHTML =
      '<div class="wfs-v3__top">' +
      '<span class="wfs-v3__brand">' +
      escapeHtml(product.vendor || "") +
      "</span>" +
      "</div>" +
      '<a class="wfs-v3__title" href="' +
      escapeHtml(productLink.href) +
      '">' +
      escapeHtml(cleanProductTitle(product.title, product.vendor)) +
      "</a>" +
      '<div class="wfs-v3__price-row">' +
      '<span class="wfs-v3__price">' +
      formatMoney(initialVariant.price || product.price) +
      "</span>" +
      '<span class="wfs-v3__compare-price"' +
      (discount ? "" : " hidden") +
      ">" +
      (discount ? formatMoney(initialVariant.compare_at_price || product.compare_at_price) : "") +
      "</span>" +
      '<span class="wfs-v3__discount"' +
      (discount ? "" : " hidden") +
      ">" +
      (discount ? "-" + discount + "%" : "") +
      "</span>" +
      "</div>" +
      '<div class="wfs-v3__options"></div>' +
      '<div class="wfs-v3__actions">' +
      '<button type="button" class="wfs-v3__button wfs-v3__button--add">' +
      (product.available ? "Add to basket" : "Sold out") +
      "</button>" +
      '<a class="wfs-v3__button wfs-v3__button--details" href="' +
      escapeHtml(productLink.href) +
      '"><span class="wfs-v3__button-text wfs-v3__button-text--desktop">See more details</span><span class="wfs-v3__button-text wfs-v3__button-text--mobile">More details</span></a>' +
      "</div>";

    wrapper.appendChild(newBody);

    renderOptionGroups(newBody, options, selectedOptions, variants, product);

    const addButton = newBody.querySelector(".wfs-v3__button--add");

    if (!product.available && addButton) {
      addButton.disabled = true;
    }

    function findSelectedVariant() {
      return variants.find(function (variant) {
        return (
          variant.options &&
          variant.options.every(function (value, index) {
            return value === selectedOptions[index];
          })
        );
      });
    }

    function updateImageForVariant(variant) {
      const variantUrl = getImageUrl(variant && variant.featured_image);

      if (!variantUrl || !state.images.length) {
        return;
      }

      const index = state.images.findIndex(function (url) {
        return cleanImageUrl(url) === cleanImageUrl(variantUrl);
      });

      if (index !== -1) {
        state.baseImageIndex = index;
        state.showImage(index);
      }
    }

    function updateAvailability() {
      renderOptionGroups(newBody, options, selectedOptions, variants, product);

      const selectedVariant = findSelectedVariant();

      if (!selectedVariant || !selectedVariant.available) {
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = "Unavailable";
        }
        return;
      }

      if (addButton) {
        addButton.disabled = false;
        addButton.textContent = "Add to basket";
      }

      updatePrice(newBody, product, selectedVariant);
      updateSaleBadge(card, product, selectedVariant);
      updateImageForVariant(selectedVariant);
    }

    newBody.addEventListener("click", function (event) {
      const control = event.target.closest("[data-option-index]");

      if (!control) {
        return;
      }

      const optionIndex = Number(control.dataset.optionIndex);
      const optionValue = control.dataset.optionValue;
      const selectedType = control.dataset.optionType;

      selectedOptions[optionIndex] = optionValue;

      newBody
        .querySelectorAll('[data-option-index="' + optionIndex + '"]')
        .forEach(function (button) {
          const selected = button.dataset.optionValue === optionValue;

          button.classList.toggle("is-selected", selected);
          button.setAttribute("aria-pressed", selected ? "true" : "false");
        });

      if (selectedType === "colour") {
        const colourName = newBody.querySelector(
          '[data-option-group="' + optionIndex + '"] .wfs-v3__colour-name'
        );

        if (colourName) {
          colourName.textContent = optionValue;
        }
      }

      updateAvailability();
    });

    addButton.addEventListener("click", function () {
      const variant = findSelectedVariant();

      if (!variant || !variant.available) {
        return;
      }

      addButton.disabled = true;
      addButton.textContent = "Adding…";

      const sizeProperty = buildSizeProperty(product, variant);
      const lineItem = {
        id: variant.id,
        quantity: 1,
      };

      // Match PDP cart line format: Black / US - 9 | UK - 6 | EU - 39 / 4E
      if (sizeProperty) {
        lineItem.properties = {
          Size: sizeProperty,
        };
      }

      fetch(shopRoot() + "cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [lineItem],
        }),
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (data) {
              throw new Error(data.description || data.message || "Unable to add item");
            });
          }

          return response.json();
        })
        .then(function (addResponse) {
          refreshCartUI(addResponse);
          addButton.disabled = false;
          addButton.textContent = "Add to basket";
        })
        .catch(function () {
          addButton.disabled = false;
          addButton.textContent = "Add to basket";
        });
    });

    updateAvailability();
    card.dataset.wfsV3 = "ready";
    schedulePlpResultStats();
  }

  function hydrateCard(card) {
    const handle = card.dataset.productHandle;

    getProduct(handle, card)
      .then(function (product) {
        transformCard(card, product);
      })
      .catch(function (error) {
        console.error("[WFS Product Card V3]", error);
        removeCardSkeleton(card);
        card.dataset.wfsV3 = "error";
      });
  }

  const intersectionObserver =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;

              intersectionObserver.unobserve(entry.target);
              hydrateCard(entry.target);
            });
          },
          {
            rootMargin: "150px 0px",
          }
        )
      : null;

  function queueCard(card) {
    if (!card || queuedCards.has(card) || card.dataset.wfsV3 === "ready") {
      return;
    }

    queuedCards.add(card);
    card.dataset.wfsV3 = "queued";

    const hasEmbeddedProduct = !!card.querySelector("script.wfs-v3-product-data");

    // Embedded JSON hydrates without a network wait — keep Liquid image visible
    // and skip the full-card skeleton flash.
    if (!hasEmbeddedProduct) {
      showCardSkeleton(card);
    }

    if (intersectionObserver) {
      intersectionObserver.observe(card);
      return;
    }

    hydrateCard(card);
  }

  function scanCards(root) {
    root = root || document;

    if (root.matches && root.matches(CARD_SELECTOR)) {
      queueCard(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(CARD_SELECTOR).forEach(queueCard);
    }
  }

  function patchWishlistButtons(root) {
    root = root || document;

    const buttons = [];

    if (root.matches && root.matches("#collection-grid .swym-button.swym-add-to-wishlist-view-product")) {
      buttons.push(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll("#collection-grid .swym-button.swym-add-to-wishlist-view-product").forEach(function (button) {
        buttons.push(button);
      });
    }

    buttons.forEach(function (button) {
      if (button.classList.contains("wfs-v3__wishlist-ready")) {
        return;
      }

      button.classList.add("wfs-v3__wishlist", "wfs-v3__wishlist-ready");
      button.style.setProperty("left", "unset", "important");
    });
  }

  function scheduleWishlistPatch() {
    patchWishlistButtons(document);
    window.setTimeout(function () {
      patchWishlistButtons(document);
    }, 100);
    window.setTimeout(function () {
      patchWishlistButtons(document);
    }, 500);
    window.setTimeout(function () {
      patchWishlistButtons(document);
    }, 1500);
  }

  function start() {
    if (!document.querySelector("#collection-grid")) {
      return;
    }

    scanCards(document);
    scheduleWishlistPatch();
    syncPlpResultStats();

    document.addEventListener("swym:collections-loaded", scheduleWishlistPatch);
    document.addEventListener("collection:reloaded", syncPlpResultStats);

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) {
            scanCards(node);
            patchWishlistButtons(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState !== "loading") {
    start();
  } else {
    document.addEventListener("DOMContentLoaded", start);
  }
})();
